import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Globe, Smartphone, Zap } from "lucide-react";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const badges = [
  { icon: Globe, label: "React", pos: "-left-6 top-10", delay: 0 },
  { icon: Smartphone, label: "React Native", pos: "-right-4 top-1/2", delay: 0.8 },
  { icon: Zap, label: "FastAPI", pos: "left-8 -bottom-5", delay: 1.6 },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-32 pb-24 px-6 lg:px-16 overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#10B981]/[0.07] blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[480px] h-[480px] rounded-full bg-[#6366F1]/[0.06] blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-16 items-center relative z-10">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="inline-flex items-center gap-2.5 border border-[#10B981]/25 bg-[#10B981]/[0.08] rounded-full px-4 py-1.5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#10B981] pulse-dot" />
            <span className="font-code text-xs text-[#10B981] tracking-wide">YENİ PROJELERE AÇIK</span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight"
          >
            <RevealLine delay={0.15}>İşletmen için</RevealLine>
            <RevealLine delay={0.3}>
              <span className="text-[#10B981]">dijital ürünler</span>
            </RevealLine>
            <RevealLine delay={0.45}>inşa ediyorum.</RevealLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-7 max-w-xl text-base md:text-lg text-[#9CA3AF] leading-relaxed"
          >
            Web siteleri ve mobil uygulamalar geliştiriyorum. Aklındaki projeyi
            başvuru formuna yaz — 24 saat içinde telefon ya da e-posta ile sana dönüş yapayım.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => window.__lenis?.scrollTo("#basvuru", { offset: -72 })}
              data-testid="hero-cta-apply"
              className="group flex items-center gap-2 bg-[#10B981] hover:bg-[#0ea371] text-[#08070D] font-semibold px-7 py-3.5 rounded-full transition-colors duration-300"
            >
              Hemen Başvur
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => window.__lenis?.scrollTo("#hizmetler", { offset: -72 })}
              data-testid="hero-cta-estimator"
              className="flex items-center gap-2 border border-white/15 hover:border-[#10B981]/50 text-white px-7 py-3.5 rounded-full transition-colors duration-300"
            >
              Hizmetleri Gör
              <ArrowDown size={16} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-14 flex gap-12"
          >
            <div data-testid="hero-stat-projects">
              <div className="font-code text-3xl font-bold text-white">30+</div>
              <div className="text-sm text-[#6B7280] mt-1">Tamamlanan Proje</div>
            </div>
            <div className="w-px bg-white/10" />
            <div data-testid="hero-stat-delivery">
              <div className="font-code text-3xl font-bold text-[#10B981]">24s</div>
              <div className="text-sm text-[#6B7280] mt-1">İçinde Geri Dönüş</div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yImg }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative hidden lg:block"
        >
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_100px_-30px_rgba(16,185,129,0.25)]">
            <img
              src="https://images.unsplash.com/photo-1753998943228-73470750c597?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Yazılım geliştirme çalışma alanı"
              className="w-full h-[460px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08070D]/80 via-transparent to-transparent" />
          </div>
          {badges.map((b) => (
            <motion.div
              key={b.label}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, delay: b.delay, ease: "easeInOut" }}
              className={`absolute ${b.pos} flex items-center gap-2 bg-[#151421]/90 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 shadow-xl`}
            >
              <b.icon size={14} className="text-[#10B981]" />
              <span className="font-code text-xs text-white">{b.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
